import { Card, CardContent } from "@affinity/components/Card";
import { bodyFont, colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import Button from "@affinity/MobiusOverrides/Button/Button";
import { RichContentComponent } from "@affinity/Overrides/Widgets/Basic/RichContent";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import React, { FC } from "react";
import styled from "styled-components";

interface Item {
    icon?: string;
    title: string;
    content: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        heading?: string;
        link: LinkFieldValue;
        label?: string;
        items: { fields: Item }[];
    };
}

export const BestDeals: FC<OwnProps> = ({ fields }) => {
    const link = useGetLink(fields.link);

    return (
        <Section>
            {fields.heading && (
                <Typography
                    variant="h2"
                    style={{
                        textAlign: "center",
                        // @ts-expect-error It's a new css property
                        textWrap: "balance",
                        color: colours.black,
                    }}
                >
                    {fields.heading}
                </Typography>
            )}
            <List>
                {fields.items.map(({ fields }, index) => (
                    <Card key={index} variant="outline" color="lightBlue" as="li">
                        {fields.icon && <Icon src={fields.icon} alt={fields.title} />}
                        {fields.title && <Title>{fields.title}</Title>}
                        <RichContentComponent fields={{ content: fields.content, padding: false }} css={{}} />
                    </Card>
                ))}
            </List>
            {fields.link?.value && fields.label && (
                <Button
                    href={link.url}
                    variant="primary"
                    sizeVariant="large"
                    icon={{
                        src: "ChevronRight",
                        position: "right",
                    }}
                >
                    {fields.label}
                </Button>
            )}
        </Section>
    );
};

const Section = styled.section`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${rem(20)};
    padding: ${rem(30)} 0;
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        gap: ${rem(30)};
        padding: ${rem(60)} 0;
    }
`;

const List = styled.ol`
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    gap: ${rem(20)};
    @media screen and (min-width: ${breakpointWidth("md")}) {
        gap: ${rem(30)};
        flex-direction: row;
    }
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        gap: ${rem(40)};
    }
    ${CardContent} {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${rem(10)};
        text-align: center;
    }
`;

const Title = styled.h3`
    margin: 0;
    font-family: ${bodyFont};
    font-weight: 700;
    text-wrap: balance;
    font-size: ${rem(18)};
    line-height: 1.1em;
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        font-size: ${rem(20)};
    }
`;

const Icon = styled.img`
    width: ${rem(50)};
    height: ${rem(50)};
    margin-bottom: ${rem(5)};
    @media screen and (min-width: ${breakpointWidth("lg")}) {
        width: ${rem(70)};
        height: ${rem(70)};
    }
`;

const bestDeals: WidgetModule = {
    component: BestDeals,
    definition: {
        group: "Basic",
        icon: "columns-3",
        fieldDefinitions: [
            {
                name: "heading",
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "items",
                displayName: "Items",
                editorTemplate: "ListField",
                getDisplay: ({ fields }) => {
                    return fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                validate: value => {
                    if (value.length > 3) {
                        return "You can only have a maximum of 3 items";
                    }
                    return null;
                },
                fieldDefinitions: [
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "content",
                        displayName: "Content",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
            {
                name: "link",
                displayName: "Button link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: "label",
                displayName: "Button label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default bestDeals;
